import React from 'react';
import { DEFAULT_LOCALE, Locale } from './config';
import { loadLocaleBundle } from './localeLoader';
import { LocaleBundle } from './types';

type I18nContextValue = {
    locale: Locale;
    t: (key: string) => string;
    languageSwitcherOptions: Record<Locale, string>;
};

const I18nContext = React.createContext<I18nContextValue | null>(null);

export function I18nProvider({
    locale,
    children,
}: {
    locale: Locale;
    children: React.ReactNode;
}) {
    const [bundle, setBundle] = React.useState<LocaleBundle | null>(null);

    React.useEffect(() => {
        let cancelled = false;

        loadLocaleBundle(locale)
            .catch(() => loadLocaleBundle(DEFAULT_LOCALE))
            .then((nextBundle) => {
                if (cancelled) {
                    return;
                }
                setBundle(nextBundle);
            });

        return () => {
            cancelled = true;
        };
    }, [locale]);

    const value = React.useMemo<I18nContextValue | null>(() => {
        if (!bundle) {
            return null;
        }

        return {
            locale,
            t: (key: string) => bundle.messages[key] ?? key,
            languageSwitcherOptions: bundle.languageSwitcherOptions,
        };
    }, [bundle, locale]);

    if (!value) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-[#f2f2f6] text-gray-500 font-sans">
                Loading...
            </div>
        );
    }

    return (
        <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
    );
}

export function useI18n(): I18nContextValue {
    const context = React.useContext(I18nContext);

    if (!context) {
        throw new Error('useI18n must be used within an I18nProvider');
    }

    return context;
}
